import React, { useEffect, useState } from "react";
import styles from './HealthSystems.module.scss';
import PageTitle from "../../../components/pageTitle/PageTitle";
import Search from "../../../components/search/Search";
import Filter from "../../../components/filter/Filter";
import Button from "../../../components/button/Button";
import ClinicalSiteCard from "../../../components/clinicalSiteCard/ClinicalSiteCard";
import axiosClient from "../../../api/axiosClient";
import LoadingOverlay from "../../../components/loadingOverlay/LoadingOverlay";
import LoadMore from "../../../components/loadMore/loadMore";
import { useToasts } from "../../../contexts/ToastContext";
import { useNavigate } from "react-router-dom";
import { formatPhoneNumber } from "../../../utils/stringUtils";

const HealthSystems = () => {
  const limit = 20;
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('All Health Systems');
  const [healthSystems, setHealthSystems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadMoreLoading, setIsLoadMoreLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const { showToast } = useToasts();

  useEffect(() => {
    const fetchHealthSystems = async () => {
      if(page === 1) {
        setIsLoading(true);
      } else {
        setIsLoadMoreLoading(true);
      }

      const params = {
        limit: limit,
        page: page,
        search: search,
      };

      if (selectedFilter && selectedFilter !== 'All Health Systems') {
        params.status = selectedFilter === 'Active';
      }

      await axiosClient.get(`health-systems/list`, {
        params: params
      })
      .then((response) => {
        if(response.data?.HealthSystems) {
          if(page === 1) {
            setHealthSystems([]);
          }
          setHealthSystems((prevData) => {
            return [...prevData, ...response.data.HealthSystems.map((system) => ({
              id: system.id,
              name: system.name,
              address: system.address ?? '',
              isActive: system.status ?? false,
              phone: system.mobileNumber ? formatPhoneNumber(system.mobileNumber) : '',
              clinicalSitesCount: system.clinicalSites ? system.clinicalSites.length : 0,
              agreementStatus: system.agreementStatus
            }))];
          });
          setTotalPages(response.data.totalPages);
        }
      })
      .catch((error) => {
        showToast(error.message, 'error');
      })
      .finally(() => {
        setIsLoading(false);
        setIsLoadMoreLoading(false);
      });
    };

    fetchHealthSystems();
  }, [page, search, selectedFilter, showToast]);

  const handleSearch = (value) => {
    setSearch(value);
    setPage(1);
  };

  const loadMore = async () => {
    setPage(page + 1);
  };

  return (
    <>
      {isLoading && <LoadingOverlay />}
      <div className={styles.header}>
        <PageTitle
          label="Health Systems"
        />
        <div className={styles.searchWrapper}>
          <Search 
            onSubmit={handleSearch}
          />
        </div>
      </div>
      <div className={styles.container}>
        <div className={styles.topBar}>
          <div className={styles.filter}>
            <Filter
              filters={[
                {
                  label: 'All Health Systems',
                  value: 'All Health Systems',
                },
                {
                  label: 'Active',
                  value: 'Active',
                },
                {
                  label: 'Inactive',
                  value: 'Inactive',
                }
              ]}
              selected={selectedFilter}
              onChange={(filter) => {
                setSelectedFilter(filter);
                setPage(1);
              }}
            />
          </div>
          <div className={styles.buttonWrapper}>
            <Button
              label="Add New Health System"
              size="medium"
              onClick={() => navigate('/health-systems/create')}
            />
          </div>
        </div>
        
        {healthSystems.length > 0 ? <div className={styles.cardList}>
          {healthSystems.map((system, index) => {
            return (
              <div className={styles.cardItem} key={`system-${index}`}>
                <ClinicalSiteCard
                  name={system.name}
                  address={system.address}
                  status={system.isActive ? 'Active' : 'Inactive'}
                  type="health-system"
                  detailItems={[
                    { label: 'Health System ID', value: system.id },
                    { label: 'Phone', value: system.phone },
                    { label: 'Clinical Sites', value: system.clinicalSitesCount },
                    { label: 'Affiliation Agreement', value: system.agreementStatus }
                  ]}
                  onClick={() => navigate(`/health-systems/edit/${system.id}`)} 
                />
              </div>
            );
          })}
          {(isLoadMoreLoading || page < totalPages) 
            && <LoadMore isLoading={isLoadMoreLoading} onClick={loadMore} />}
        </div> : !isLoading && <div className={styles.empty}>
          <p>No health systems found.</p>
        </div>}
      </div>
    </>
  );
};

export default HealthSystems;
